/**
 * What an error of a write means for the command that caused it (batch 8 T12c1, #3033).
 *
 *  - `refused`: the server (or this client, before anything was sent) decided: nothing happened because of this request. A 4xx that is
 *    not a session guard. The user may correct and try again, as a new intent.
 *  - `unknown`: a lost connection, a timeout, a 5xx, an expired or locked session. The request may have been applied.
 *    Nothing is repeated on its own.
 */

import { isLocalRefusal } from './dispatchSession';

interface ErrorLike {
  status?: unknown;
  data?: unknown;
}

const SESSION_OR_TIMEOUT_STATUSES = [401, 403, 408, 423, 425];

export function statusOf(error: unknown): number | null {
  const { status } = (error ?? {}) as ErrorLike;
  return typeof status === 'number' ? status : null;
}

export interface ErrorBody {
  code?: string;
  details?: Record<string, unknown>;
}

/** The `code` (and `details`) of an API error body; nothing else of the server's prose is read. */
export function bodyOf(error: unknown): ErrorBody {
  const { data } = (error ?? {}) as ErrorLike;
  if (typeof data !== 'object' || data === null) return {};
  const { code, details } = data as { code?: unknown; details?: unknown };
  return {
    ...(typeof code === 'string' ? { code } : {}),
    ...(typeof details === 'object' && details !== null && !Array.isArray(details)
      ? { details: details as Record<string, unknown> }
      : {}),
  };
}

/** True when the server decided, or the client refused before sending: nothing can be pending. */
export function isDefiniteRefusal(error: unknown): boolean {
  if (isLocalRefusal(error)) return true;
  const status = statusOf(error);
  if (status === null || status < 400 || status >= 500) return false;
  return !SESSION_OR_TIMEOUT_STATUSES.includes(status);
}

/** The write may have been applied and nobody knows. */
export const isUnknownOutcome = (error: unknown): boolean => !isDefiniteRefusal(error);

/** `stale_review`: the document changed after it was reviewed. */
export const isStaleReview = (error: unknown): boolean =>
  statusOf(error) === 409 && bodyOf(error).code === 'stale_review';
